import type { Author, ResolveResponse } from "./types";

const MAX_AVATAR_BYTES = 64 * 1024;
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/avif"]);

function bytesToDataURL(bytes: Uint8Array, contentType: string): string {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x4000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x4000));
  }
  return `data:${contentType};base64,${btoa(binary)}`;
}

async function fetchEmbeddedAvatar(url: string): Promise<string> {
  const response = await fetch(`/avatar-export?url=${encodeURIComponent(url)}`, { credentials: "same-origin" });
  const contentType = (response.headers.get("content-type") || "").split(";", 1)[0].toLowerCase();
  const declaredLength = Number(response.headers.get("content-length") || 0);
  if (!response.ok || !ALLOWED_TYPES.has(contentType) || declaredLength > MAX_AVATAR_BYTES) throw new Error("AVATAR_EXPORT_FAILED");
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength > MAX_AVATAR_BYTES) throw new Error("AVATAR_EXPORT_FAILED");
  return bytesToDataURL(bytes, contentType);
}

export async function embedAvatarImages(result: ResolveResponse): Promise<ResolveResponse> {
  const urls = new Set<string>();
  if (result.author.avatarUrl) urls.add(result.author.avatarUrl);
  if (result.quotedPost?.author.avatarUrl) urls.add(result.quotedPost.author.avatarUrl);
  for (const reply of result.replies) if (reply.author.avatarUrl) urls.add(reply.author.avatarUrl);
  for (const post of result.profile?.posts || []) {
    if (post.author.avatarUrl) urls.add(post.author.avatarUrl);
    if (post.quotedPost?.author.avatarUrl) urls.add(post.quotedPost.author.avatarUrl);
  }

  const embedded = new Map<string, string>();
  await Promise.all([...urls].map(async (url) => {
    try { embedded.set(url, await fetchEmbeddedAvatar(url)); } catch { embedded.set(url, url); }
  }));
  const mapAuthor = (author: Author): Author => author.avatarUrl
    ? { ...author, avatarUrl: embedded.get(author.avatarUrl) || author.avatarUrl }
    : { ...author };

  return {
    ...result,
    author: mapAuthor(result.author),
    quotedPost: result.quotedPost ? { ...result.quotedPost, author: mapAuthor(result.quotedPost.author) } : null,
    replies: result.replies.map((reply) => ({ ...reply, author: mapAuthor(reply.author) })),
    profile: result.profile ? {
      ...result.profile,
      posts: result.profile.posts.map((post) => ({
        ...post,
        author: mapAuthor(post.author),
        quotedPost: post.quotedPost ? { ...post.quotedPost, author: mapAuthor(post.quotedPost.author) } : null
      }))
    } : undefined
  };
}
